import React, { useEffect, useState } from "react"
import axios from "axios"
const API = process.env.REACT_APP_API_URL || "http://localhost:3001/api"

interface Resposta {
  id: string
  modelo_nome: string
  tipo: string
  ativo_nome: string | null
  placa: string | null
  usuario_nome: string | null
  motorista_nome: string | null
  km_informado: number | null
  observacoes_gerais: string | null
  preenchido_em: string | null
  criado_em: string
}

interface ItemResposta {
  id: string
  pergunta: string
  tipo_resposta: string
  valor_bool: boolean | null
  valor_nota: number | null
  valor_texto: string | null
  sub_resposta: string | null
  observacao: string | null
  fotos: { url: string }[]
  necessita_atencao: boolean
  exige_foto: boolean
}

const TIPOS = [
  { valor: "", label: "Todos os tipos" },
  { valor: "chegada", label: "Chegada / Retorno" },
  { valor: "vistoria", label: "Vistoria Preventiva" },
  { valor: "entrega", label: "Entrega (aluguel)" },
  { valor: "recebimento", label: "Recebimento (aluguel)" },
]

export default function ChecklistRespostas() {
  const [respostas, setRespostas] = useState<Resposta[]>([])
  const [carregando, setCarregando] = useState(true)
  const [busca, setBusca] = useState("")
  const [filtroTipo, setFiltroTipo] = useState("")
  const [expandido, setExpandido] = useState<string | null>(null)
  const [detalhe, setDetalhe] = useState<Record<string, ItemResposta[]>>({})
  const [carregandoDetalhe, setCarregandoDetalhe] = useState<string | null>(null)

  const token = () => localStorage.getItem("moobi_token")
  const headers = () => ({ Authorization: `Bearer ${token()}` })

  const carregar = async () => {
    const r = await axios.get(`${API}/checklists`, { headers: headers(), params: filtroTipo ? { tipo: filtroTipo } : {} })
    setRespostas(r.data)
  }

  useEffect(() => {
    (async () => {
      setCarregando(true)
      await carregar()
      setCarregando(false)
    })()
  }, [filtroTipo])

  const alternarExpandir = async (id: string) => {
    if (expandido === id) { setExpandido(null); return }
    setExpandido(id)
    if (!detalhe[id]) {
      setCarregandoDetalhe(id)
      try {
        const r = await axios.get(`${API}/checklists/${id}`, { headers: headers() })
        setDetalhe(prev => ({ ...prev, [id]: r.data.itens }))
      } catch { setDetalhe(prev => ({ ...prev, [id]: [] })) }
      setCarregandoDetalhe(null)
    }
  }

  const abrirPdf = (id: string) => {
    window.open(`${API}/checklists/${id}/pdf?token=${token()}`, "_blank")
  }

  const termo = busca.trim().toLowerCase()
  const respostasFiltradas = respostas.filter(r =>
    !termo || [r.ativo_nome, r.placa, r.modelo_nome, r.usuario_nome, r.motorista_nome]
      .filter(Boolean).some(campo => String(campo).toLowerCase().includes(termo))
  )

  const corFundo = "#0d1b2e", corCard = "#1e2f4a", corBorda = "#2d4166", corTexto = "#f1f5f9", corMuted = "#94a3b8", corAccent = "#0ea5e9"

  const estiloInput: React.CSSProperties = {
    padding: "9px 12px", borderRadius: 8, border: `1px solid ${corBorda}`,
    background: corCard, color: corTexto, fontSize: 13, boxSizing: "border-box",
  }

  const nomeTipo = (t: string) => TIPOS.find(x => x.valor === t)?.label || t

  const rotuloResposta = (it: ItemResposta) => {
    if (it.tipo_resposta === "foto") return it.valor_texto === "nao_possui" ? "Não possui" : (it.fotos && it.fotos.length > 0 ? "Foto" : "—")
    if (it.tipo_resposta === "sim_nao") return it.valor_bool === true ? "Conforme" : it.valor_bool === false ? "Não conforme" : "—"
    if (it.tipo_resposta === "nota") return it.valor_nota != null ? `Nota ${it.valor_nota}` : "—"
    return it.valor_texto || "—"
  }

  if (carregando) return <div style={{ padding: 32, color: corMuted }}>Carregando...</div>

  return (
    <div style={{ padding: 24, height: "100%", overflow: "auto", background: corFundo, color: corTexto }}>
      <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>Checklists Preenchidos</h2>
      <p style={{ fontSize: 12, color: corMuted, marginBottom: 20 }}>
        Histórico de checklists enviados pelos motoristas pelo app.
      </p>

      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        <input style={{ ...estiloInput, minWidth: 280 }} placeholder="Buscar por veículo, placa, motorista..."
          value={busca} onChange={e => setBusca(e.target.value)} />
        <select style={estiloInput} value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)}>
          {TIPOS.map(t => <option key={t.valor} value={t.valor}>{t.label}</option>)}
        </select>
      </div>

      {respostasFiltradas.length === 0 ? (
        <div style={{ color: corMuted, fontSize: 13 }}>Nenhum checklist encontrado.</div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${corBorda}` }}>
                <th style={{ textAlign: "left", padding: "10px 8px", color: corMuted, fontWeight: 400 }}>Veículo</th>
                <th style={{ textAlign: "left", padding: "10px 8px", color: corMuted, fontWeight: 400 }}>Modelo / Tipo</th>
                <th style={{ textAlign: "left", padding: "10px 8px", color: corMuted, fontWeight: 400 }}>Data</th>
                <th style={{ textAlign: "left", padding: "10px 8px", color: corMuted, fontWeight: 400 }}>Preenchido por</th>
                <th style={{ textAlign: "right", padding: "10px 8px", color: corMuted, fontWeight: 400 }}>Km</th>
                <th style={{ textAlign: "right", padding: "10px 8px", color: corMuted, fontWeight: 400 }}>PDF</th>
              </tr>
            </thead>
            <tbody>
              {respostasFiltradas.map(r => {
                const aberta = expandido === r.id
                const itensDetalhe = detalhe[r.id] || []
                return (
                  <React.Fragment key={r.id}>
                    <tr style={{ borderBottom: `1px solid ${corCard}`, cursor: "pointer" }} onClick={() => alternarExpandir(r.id)}>
                      <td style={{ padding: "10px 8px" }}>
                        <div style={{ fontWeight: 600 }}>{r.ativo_nome || "—"}</div>
                        {!!r.placa && <div style={{ fontSize: 11, color: corMuted }}>{r.placa}</div>}
                      </td>
                      <td style={{ padding: "10px 8px", color: corMuted }}>
                        {r.modelo_nome}
                        <div style={{ fontSize: 11 }}>{nomeTipo(r.tipo)}</div>
                      </td>
                      <td style={{ padding: "10px 8px", color: corMuted }}>{new Date(r.preenchido_em || r.criado_em).toLocaleString("pt-BR")}</td>
                      <td style={{ padding: "10px 8px", color: corMuted }}>{r.usuario_nome || r.motorista_nome || "—"}</td>
                      <td style={{ padding: "10px 8px", textAlign: "right", color: corMuted }}>{r.km_informado != null ? `${Math.round(r.km_informado).toLocaleString("pt-BR")} km` : "—"}</td>
                      <td style={{ padding: "10px 8px", textAlign: "right" }}>
                        <span style={{ color: corAccent, cursor: "pointer" }} onClick={e => { e.stopPropagation(); abrirPdf(r.id) }}>📄 Ver PDF</span>
                      </td>
                    </tr>
                    {aberta && (
                      <tr style={{ borderBottom: `1px solid ${corCard}`, background: "#0a1626" }}>
                        <td colSpan={6} style={{ padding: 16 }}>
                          {carregandoDetalhe === r.id ? (
                            <div style={{ color: corMuted, fontSize: 12 }}>Carregando...</div>
                          ) : (
                            <>
                              {!!r.observacoes_gerais && (
                                <div style={{ fontSize: 12, color: corMuted, marginBottom: 10 }}>📝 {r.observacoes_gerais}</div>
                              )}
                              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                {itensDetalhe.map(it => (
                                  <div key={it.id} style={{ background: corCard, borderRadius: 8, padding: "8px 12px", fontSize: 12 }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                                      <span style={{ color: corTexto }}>{it.pergunta}</span>
                                      <span style={{ color: it.valor_texto === "nao_possui" ? "#fbbf24" : it.valor_bool === false ? "#f87171" : it.valor_bool === true ? "#4ade80" : corMuted, fontWeight: 600, whiteSpace: "nowrap" }}>
                                        {rotuloResposta(it)}{it.sub_resposta ? ` (${it.sub_resposta})` : ""}
                                      </span>
                                    </div>
                                    {!!it.observacao && <div style={{ color: corMuted, marginTop: 4 }}>{it.observacao}</div>}
                                    {it.fotos && it.fotos.length > 0 && (
                                      <div style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
                                        {it.fotos.map((f, i) => (
                                          <a key={i} href={f.url.startsWith("http") ? f.url : `https://track.moobifleet.com.br${f.url}`} target="_blank" rel="noreferrer">
                                            <img src={f.url.startsWith("http") ? f.url : `${API}/fotos/miniatura/${f.url.split("/").pop()}`} alt="foto" loading="lazy" decoding="async"
                                              onError={e => { const img = e.currentTarget; const original = f.url.startsWith("http") ? f.url : `https://track.moobifleet.com.br${f.url}`; if (img.src !== original) img.src = original }}
                                              style={{ width: 56, height: 56, borderRadius: 6, objectFit: "cover", border: `1px solid ${corBorda}` }} />
                                          </a>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
